import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import Button from '../../components/common/Button';
import Icon from '../../components/common/Icon';
import MediatorAssignmentModal from '../../components/common/MediatorAssignmentModal';
import SearchableSelect from '../../components/common/SearchableSelect';
import { productAPI, brandAPI, platformAPI, enumAPI } from '../../services/api';
import PaginationBar from '../../components/common/PaginationBar';

const formatFeedbackLabel = (value) =>
  value
    ? value
        .toString()
        .replace(/_/g, ' ')
        .toLowerCase()
        .replace(/\b\w/g, (char) => char.toUpperCase())
    : '-';

const resolveImageUrl = (value) => {
  if (!value) return '';

  try {
    const url = new URL(value);

    if (url.hostname.includes('drive.google.com')) {
      const filePathMatch = url.pathname.match(/\/file\/d\/([^/]+)/);
      const fileId = filePathMatch?.[1] || url.searchParams.get('id');

      if (fileId) {
        return `https://drive.google.com/thumbnail?id=${fileId}&sz=w1000`;
      }
    }
  } catch (error) {
    return value;
  }

  return value;
};

const createEmptyFeedbackConfig = () => ({
  localId: `${Date.now()}-${Math.random()}`,
  feedbackType: '',
  payoutType: 'LESS',
  payoutValue: '',
  totalSlots: '',
  remainingSlots: '',
  remainingTouched: false
});

const ImageField = ({ label, value, onChange, onPickFile, onClear, fileName, inputId }) => (
  <div>
    <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: '600', color: '#475569' }}>{label}</label>
    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
      <input type="text" value={value} onChange={(event) => onChange(event.target.value)} placeholder="Paste image URL or upload" style={{ flex: 1, padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px' }} />
      <label htmlFor={inputId} style={{ padding: '10px 12px', borderRadius: '8px', border: '1px solid #d1d5db', background: '#f8fafc', fontSize: '12px', fontWeight: '600', color: '#475569', cursor: 'pointer' }}>
        Upload
      </label>
      <input id={inputId} type="file" accept="image/*" onChange={onPickFile} style={{ display: 'none' }} />
      {(value || fileName) && (
        <button
          type="button"
          onClick={onClear}
          style={{
            width: '38px',
            height: '38px',
            borderRadius: '10px',
            border: '1px solid #fecaca',
            background: '#fff1f2',
            color: '#dc2626',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
          title="Remove image"
        >
          <Icon name="x" size={15} />
        </button>
      )}
    </div>
    {fileName && <div style={{ marginTop: '6px', fontSize: '12px', color: '#64748b' }}>{fileName}</div>}
    {value && (
      <div style={{ marginTop: '10px', width: '72px', height: '72px', borderRadius: '10px', overflow: 'hidden', border: '1px solid #e2e8f0', background: '#f8fafc' }}>
        <img src={resolveImageUrl(value)} alt={label} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      </div>
    )}
  </div>
);

const FeedbackConfigEditor = ({ feedbackConfigs, setFeedbackConfigs, feedbackTypeOptions, error, setError }) => {
  const updateFeedbackConfig = (localId, field, value) => {
    setError('');
    setFeedbackConfigs((current) =>
      current.map((config) => {
        if (config.localId !== localId) return config;
        const nextConfig = { ...config, [field]: value };
        if (field === 'remainingSlots') nextConfig.remainingTouched = true;
        return nextConfig;
      })
    );
  };

  const addFeedbackConfig = () => {
    setError('');
    setFeedbackConfigs((current) => [...current, createEmptyFeedbackConfig()]);
  };

  const removeFeedbackConfig = (localId) => {
    setError('');
    setFeedbackConfigs((current) => (current.length === 1 ? current : current.filter((config) => config.localId !== localId)));
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontSize: '14px', fontWeight: '600', color: '#374151' }}>Feedback Configurations</div>
          <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>Each feedback type gets its own payout mode, less value, and slots.</div>
        </div>
        <Button type="button" size="sm" onClick={addFeedbackConfig}>
          <Icon name="plus" size={14} style={{ marginRight: '8px' }} />
          Add Row
        </Button>
      </div>

      {feedbackConfigs.map((config, index) => (
        <div key={config.localId} style={{ padding: '16px', borderRadius: '12px', border: '1px solid #e5e7eb', background: '#f8fafc' }}>
          {(() => {
            const usedTypes = new Set(
              feedbackConfigs
                .filter((item) => item.localId !== config.localId && item.feedbackType)
                .map((item) => item.feedbackType)
            );
            return (
          <div style={{ display: 'grid', gridTemplateColumns: '1.1fr 0.9fr 0.9fr 0.8fr 0.8fr auto', gap: '12px', alignItems: 'end' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: '600', color: '#475569' }}>Feedback Type *</label>
              <select value={config.feedbackType} onChange={(event) => updateFeedbackConfig(config.localId, 'feedbackType', event.target.value)} style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px' }}>
                <option value="">Select type</option>
                {feedbackTypeOptions.map((option) => <option key={option.value} value={option.value} disabled={usedTypes.has(option.value)}>{option.label}</option>)}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: '600', color: '#475569' }}>Payout Type *</label>
              <select value={config.payoutType} onChange={(event) => updateFeedbackConfig(config.localId, 'payoutType', event.target.value)} style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px' }}>
                <option value="LESS">LESS</option>
                <option value="COMMISSION">COMMISSION</option>
              </select>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: '600', color: '#475569' }}>Less Value *</label>
              <input type="number" step="0.01" value={config.payoutValue} onChange={(event) => updateFeedbackConfig(config.localId, 'payoutValue', event.target.value)} placeholder="0.00" style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px' }} />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: '600', color: '#475569' }}>Total Slots *</label>
              <input type="number" min="0" value={config.totalSlots} onChange={(event) => updateFeedbackConfig(config.localId, 'totalSlots', event.target.value)} placeholder="0" style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px' }} />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: '600', color: '#475569' }}>Remaining Slots *</label>
              <input type="number" min="0" value={config.remainingSlots} onChange={(event) => updateFeedbackConfig(config.localId, 'remainingSlots', event.target.value)} placeholder="0" style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px' }} />
            </div>
            <button type="button" onClick={() => removeFeedbackConfig(config.localId)} disabled={feedbackConfigs.length === 1} style={{ width: '38px', height: '38px', borderRadius: '10px', border: '1px solid #fecaca', background: feedbackConfigs.length === 1 ? '#f8fafc' : '#fff1f2', color: feedbackConfigs.length === 1 ? '#94a3b8' : '#ef4444', cursor: feedbackConfigs.length === 1 ? 'not-allowed' : 'pointer' }} title={feedbackConfigs.length === 1 ? 'At least one feedback config is required' : `Remove row ${index + 1}`}>
              <Icon name="trash-2" size={15} />
            </button>
          </div>
            );
          })()}

        </div>
      ))}

      {error && <p style={{ color: '#dc2626', fontSize: '12px', margin: 0 }}>{error}</p>}
    </div>
  );
};

const AdminProducts = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [brands, setBrands] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [dealFeedbackTypes, setDealFeedbackTypes] = useState([]);
  const [searchForm, setSearchForm] = useState({ productName: '', brandId: '', platformId: '', active: '' });
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [editFeedbackConfigs, setEditFeedbackConfigs] = useState([createEmptyFeedbackConfig()]);
  const [feedbackConfigError, setFeedbackConfigError] = useState('');
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [editProductImageFile, setEditProductImageFile] = useState(null);
  const [editProductImagePreview, setEditProductImagePreview] = useState('');
  const [savingEditProduct, setSavingEditProduct] = useState(false);
  const [pagination, setPagination] = useState({ currentPage: 0, totalPages: 0, totalItems: 0, pageSize: 10, hasNext: false, hasPrevious: false });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchActiveBrands();
    fetchActivePlatforms();
    fetchEnums();
    fetchProducts();
  }, []);

  const feedbackTypeOptions = useMemo(
    () =>
      dealFeedbackTypes.map((type) => {
        const value = typeof type === 'string' ? type : type.code;
        const label = typeof type === 'string' ? formatFeedbackLabel(type) : type.label || formatFeedbackLabel(type.code);
        return { value, label };
      }),
    [dealFeedbackTypes]
  );
  const brandOptions = brands.map((brand) => ({ value: brand.id, label: brand.brandName || brand.name }));
  const platformOptions = platforms.map((platform) => ({ value: platform.id, label: platform.platformName || platform.name }));

  const fetchEnums = async () => {
    try {
      const response = await enumAPI.getDealFeedbackType();
      setDealFeedbackTypes(response.data || []);
    } catch (error) {
      console.error('Failed to fetch feedback types:', error);
    }
  };

  const fetchActiveBrands = async () => {
    try {
      const response = await brandAPI.getActiveBrands();
      setBrands(response.data.data || response.data || []);
    } catch (error) {
      console.error('Failed to fetch brands:', error);
    }
  };

  const fetchActivePlatforms = async () => {
    try {
      const response = await platformAPI.getPlatforms({ active: true });
      setPlatforms(response.data.data || response.data.content || response.data || []);
    } catch (error) {
      console.error('Failed to fetch platforms:', error);
    }
  };

  const fetchProducts = async (page = 0, currentSearchForm = searchForm) => {
    setLoading(true);
    try {
      const payload = {
        ...currentSearchForm,
        page,
        size: pagination.pageSize,
      };
      if (payload.active === '' || payload.active === null || payload.active === undefined) {
        delete payload.active;
      } else {
        payload.active = payload.active === true || payload.active === 'true';
      }
      const response = await productAPI.searchProducts(payload);
      const data = response.data;
      if (data.data) {
        setProducts(data.data);
        setPagination((prev) => ({ ...prev, currentPage: data.pageNumber || 0, totalPages: data.totalPages || 0, totalItems: data.totalElements || 0, hasNext: data.hasNext || false, hasPrevious: data.hasPrevious || false }));
      } else {
        const list = Array.isArray(data) ? data : [];
        setProducts(list);
        setPagination((prev) => ({ ...prev, currentPage: page, totalPages: 1, totalItems: list.length, hasNext: false, hasPrevious: page > 0 }));
      }
    } catch (error) {
      console.error('Failed to fetch products:', error);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleProductImagePick = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setEditProductImageFile(file);
    setEditProductImagePreview(URL.createObjectURL(file));
    event.target.value = '';
  };

  const clearEditProductImage = () => {
    setEditProductImageFile(null);
    setEditProductImagePreview('');
    setEditFormData((current) => ({ ...current, imageUrl: '' }));
  };

  const validateFeedbackConfigs = (feedbackConfigs) => {
    if (!feedbackConfigs.length) return 'Add at least one feedback configuration.';
    const usedTypes = new Set();
    for (const config of feedbackConfigs) {
      if (!config.feedbackType) return 'Select a feedback type for every feedback configuration.';
      if (usedTypes.has(config.feedbackType)) return `Duplicate feedback type "${formatFeedbackLabel(config.feedbackType)}" is not allowed.`;
      usedTypes.add(config.feedbackType);
      if (!config.payoutType) return `Select a payout type for ${formatFeedbackLabel(config.feedbackType)}.`;
      const payoutValue = Number(config.payoutValue);
      const totalSlots = Number(config.totalSlots);
      const remainingSlots = Number(config.remainingSlots);
      if (Number.isNaN(payoutValue) || payoutValue < 0) return `Enter a valid less value for ${formatFeedbackLabel(config.feedbackType)}.`;
      if (Number.isNaN(totalSlots) || totalSlots < 0) return `Enter valid total slots for ${formatFeedbackLabel(config.feedbackType)}.`;
      if (Number.isNaN(remainingSlots) || remainingSlots < 0 || remainingSlots > totalSlots) return `Remaining slots must be between 0 and total slots for ${formatFeedbackLabel(config.feedbackType)}.`;
    }
    return '';
  };

  const handleSearchChange = (field, value) => setSearchForm((prev) => ({ ...prev, [field]: value }));
  const handleSearch = (event) => { event.preventDefault(); fetchProducts(0, searchForm); };
  const handleClearSearch = () => {
    const cleared = { productName: '', brandId: '', platformId: '', active: '' };
    setSearchForm(cleared);
    fetchProducts(0, cleared);
  };
  const handleDropdownToggle = (id) => setActiveDropdown(activeDropdown === id ? null : id);

  const handleView = async (product) => {
    try {
      const response = await productAPI.getProduct(product.id);
      setSelectedProduct(response.data.data || response.data);
      setShowViewModal(true);
      setActiveDropdown(null);
    } catch (error) {
      toast.error('Failed to fetch details');
    }
  };

  const handleEdit = async (product) => {
    try {
      const response = await productAPI.getProduct(product.id);
      const data = response.data.data || response.data;
      setSelectedProduct(data);
      setEditFormData({ ...data, link: data.link || '', imageUrl: data.imageUrl || '', restrictedToAssignedMediators: data.restrictedToAssignedMediators !== false, productAmount: data.productAmount ?? '' });
      setEditProductImageFile(null);
      setEditProductImagePreview('');
      setEditFeedbackConfigs(
        (data.feedbackConfigs || []).length
          ? data.feedbackConfigs.map((config) => ({
              localId: `${config.id || config.feedbackType}-${Math.random()}`,
              id: config.id,
              feedbackType: config.feedbackType || '',
              payoutType: config.payoutType || 'LESS',
              payoutValue: config.payoutValue ?? '',
              totalSlots: config.totalSlots ?? '',
              remainingSlots: config.remainingSlots ?? '',
              imageUrl: config.imageUrl || '',
              remainingTouched: true
            }))
          : [createEmptyFeedbackConfig()]
      );
      setFeedbackConfigError('');
      setShowEditModal(true);
      setActiveDropdown(null);
    } catch (error) {
      toast.error('Failed to fetch details');
    }
  };

  const handleAssignMediator = (product) => {
    if (product.restrictedToAssignedMediators === false) {
      toast('This product is handled through Available Deals, so manual assignment is not needed.', { icon: 'ℹ️' });
      return;
    }
    setSelectedProduct(product);
    setShowAssignModal(true);
    setActiveDropdown(null);
  };

  const handleDelete = (product) => {
    setSelectedProduct(product);
    setShowDeleteModal(true);
    setActiveDropdown(null);
  };

  const handleDeleteConfirm = async () => {
    try {
      await productAPI.deleteProduct(selectedProduct.id);
      toast.success('Product deleted successfully');
      setShowDeleteModal(false);
      setSelectedProduct(null);
      fetchProducts(pagination.currentPage);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete product');
    }
  };

  const handleEditSubmit = async (event) => {
    event.preventDefault();
    const feedbackError = validateFeedbackConfigs(editFeedbackConfigs);
    if (feedbackError) {
      setFeedbackConfigError(feedbackError);
      toast.error(feedbackError);
      return;
    }

    if (savingEditProduct) {
      return;
    }

    setSavingEditProduct(true);
    try {
      const productPayload = {
        ...editFormData,
        productAmount: parseFloat(editFormData.productAmount || 0),
        feedbackConfigs: editFeedbackConfigs.map((config) => ({
          id: config.id,
          feedbackType: config.feedbackType,
          payoutType: config.payoutType,
          payoutValue: parseFloat(config.payoutValue || 0),
          totalSlots: parseInt(config.totalSlots, 10) || 0,
          remainingSlots: parseInt(config.remainingSlots, 10) || 0,
          imageUrl: config.imageUrl || ''
        }))
      };

      const requestBody = new FormData();
      requestBody.append('data', new Blob([JSON.stringify(productPayload)], { type: 'application/json' }));
      if (editProductImageFile) {
        requestBody.append('image', editProductImageFile);
      }

      await productAPI.updateProduct(selectedProduct.id, requestBody);

      toast.success('Product updated successfully');
      setShowEditModal(false);
      setSelectedProduct(null);
      setEditFormData({});
      setEditProductImageFile(null);
      setEditProductImagePreview('');
      setEditFeedbackConfigs([createEmptyFeedbackConfig()]);
      fetchProducts(pagination.currentPage);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update product');
    } finally {
      setSavingEditProduct(false);
    }
  };

  const getStatusBadge = (active) => active ? { color: '#10b981', bg: '#d1fae5', label: 'Active' } : { color: '#6b7280', bg: '#f3f4f6', label: 'Inactive' };
  const getRestrictionBadge = (restricted) => restricted === false ? { color: '#0369a1', bg: '#e0f2fe', label: 'Open to all mediators' } : { color: '#7c3aed', bg: '#ede9fe', label: 'Restricted to assigned mediators' };

  const PaginationComp = () => (
    <PaginationBar
      page={pagination.currentPage}
      totalPages={pagination.totalPages}
      totalElements={pagination.totalItems}
      pageSize={pagination.pageSize}
      onPageChange={(page) => fetchProducts(page, searchForm)}
      style={{ padding: '16px 20px', borderTop: '1px solid #e2e8f0' }}
    />
  );


  return (
    <div className="admin-page-shell" style={{ display: 'flex', flexDirection: 'column', height: '100vh', width: '100%', overflow: 'hidden' }}>
      <div style={{ padding: '16px 24px 12px 24px', backgroundColor: '#f8fafc', flexShrink: 0, position: 'sticky', top: 0, zIndex: 40 }}>
        <div className="admin-page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <div>
            <h2 style={{ fontSize: '20px', fontWeight: '700', margin: 0 }}>Product Management</h2>
            <p style={{ fontSize: '14px', color: '#64748b', margin: '4px 0 0 0' }}>Manage product pricing, images, mediator access, and feedback-specific slots.</p>
          </div>
          <Button onClick={() => navigate('/admin/create-product')} icon="plus">Add Product</Button>
        </div>

        <div style={{ background: '#ffffff', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', position: 'relative', zIndex: 50, overflow: 'visible' }}>
          <form onSubmit={handleSearch}>
            <div className="admin-search-grid" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr auto auto', gap: '12px', alignItems: 'end' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>Product Name</label>
                <input type="text" placeholder="Search name, code, or ASIN..." value={searchForm.productName} onChange={(event) => handleSearchChange('productName', event.target.value)} style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px' }} />
              </div>
              <div style={{ position: 'relative', zIndex: 3 }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>Brand</label>
                <SearchableSelect
                  options={brandOptions}
                  value={searchForm.brandId}
                  onChange={(value) => handleSearchChange('brandId', value)}
                  placeholder="All Brands"
                  searchPlaceholder="Type brand name..."
                />
              </div>
              <div style={{ position: 'relative', zIndex: 3 }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>Platform</label>
                <SearchableSelect
                  options={platformOptions}
                  value={searchForm.platformId}
                  onChange={(value) => handleSearchChange('platformId', value)}
                  placeholder="All Platforms"
                  searchPlaceholder="Type platform name..."
                />
              </div>
              <div style={{ position: 'relative', zIndex: 3 }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>Status</label>
                <SearchableSelect
                  options={[
                    { value: 'true', label: 'Active' },
                    { value: 'false', label: 'Inactive' },
                  ]}
                  value={searchForm.active === '' || searchForm.active === null || searchForm.active === undefined ? '' : String(searchForm.active)}
                  onChange={(value) => handleSearchChange('active', value)}
                  placeholder="All Statuses"
                  searchPlaceholder="Active or Inactive..."
                />
              </div>
              <Button type="submit">Search</Button>
              <Button type="button" variant="secondary" onClick={handleClearSearch}>Clear</Button>
            </div>
          </form>
        </div>
      </div>

      <div className="admin-table-wrap" style={{ flex: 1, padding: '0 24px 24px 24px', overflow: 'hidden', display: 'flex', flexDirection: 'column', position: 'relative', zIndex: 1 }}>
        <div style={{ background: '#ffffff', borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <p className="mobile-scroll-hint">Swipe horizontally to see all columns</p>
          <div className="table-scroll-container" style={{ flex: 1, overflow: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '1500px' }}>
              <thead style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', position: 'sticky', top: 0, zIndex: 2 }}>
                <tr>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase' }}>Product</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase' }}>Code / ASIN</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase' }}>Brand / Platform</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase' }}>Amount</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase' }}>Mediator Access</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase' }}>Feedback Configs</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase' }}>Status</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={8} style={{ padding: '40px', textAlign: 'center' }}>Loading...</td></tr>
                ) : products.length === 0 ? (
                  <tr><td colSpan={8} style={{ padding: '40px', textAlign: 'center' }}>No products found</td></tr>
                ) : (
                  products.map((product) => {
                    const statusBadge = getStatusBadge(product.active);
                    const restrictionBadge = getRestrictionBadge(product.restrictedToAssignedMediators);
                    const configs = product.feedbackConfigs || [];
                    return (
                      <tr key={product.id} style={{ borderBottom: '1px solid #f1f5f9', verticalAlign: 'top' }}>
                        <td style={{ padding: '12px 16px' }}>
                          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                            <div style={{ width: '64px', height: '64px', borderRadius: '10px', overflow: 'hidden', border: '1px solid #e2e8f0', background: '#f8fafc', flexShrink: 0 }}>
                              {product.imageUrl ? <img src={resolveImageUrl(product.imageUrl)} alt={product.productName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#cbd5e1' }}><Icon name="image" size={18} /></div>}
                            </div>
                            <div>
                              <div style={{ fontSize: '14px', fontWeight: '600', color: '#0f172a' }}>{product.productName}</div>
                              {product.link && <div style={{ marginTop: '4px', fontSize: '12px', color: '#64748b', wordBreak: 'break-all' }}>{product.link}</div>}
                            </div>
                          </div>
                        </td>
                        <td style={{ padding: '12px 16px', fontSize: '14px' }}>
                          <div style={{ fontWeight: 600 }}>{product.productCode}</div>
                          <div style={{ marginTop: '4px', fontSize: '12px', color: '#64748b' }}>
                            {product.asinNumber ? `ASIN: ${product.asinNumber}` : 'ASIN: —'}
                          </div>
                        </td>
                        <td style={{ padding: '12px 16px', fontSize: '13px' }}>
                          <div>{product.brandName || '-'}</div>
                          <div style={{ color: '#64748b', marginTop: '4px' }}>{product.platformName || '-'}</div>
                        </td>
                        <td style={{ padding: '12px 16px', fontSize: '13px' }}>₹{product.productAmount ?? '-'}</td>
                        <td style={{ padding: '12px 16px', fontSize: '13px' }}>
                          <span style={{ display: 'inline-flex', padding: '4px 8px', borderRadius: '999px', background: restrictionBadge.bg, color: restrictionBadge.color, fontSize: '12px', fontWeight: '600' }}>{restrictionBadge.label}</span>
                          <div style={{ marginTop: '8px', color: '#64748b' }}>{product.restrictedToAssignedMediators === false ? 'Open deal: all active mediators can submit orders.' : 'Only assigned mediators can submit this deal.'}</div>
                        </td>
                        <td style={{ padding: '12px 16px', fontSize: '13px' }}>
                          {configs.length === 0 ? (
                            <span style={{ color: '#94a3b8' }}>No feedback configs</span>
                          ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                              {configs.map((config) => (
                                <div key={config.id || config.feedbackType} style={{ display: 'flex', gap: '10px', padding: '8px 10px', borderRadius: '10px', background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                                  <div style={{ width: '44px', height: '44px', borderRadius: '8px', overflow: 'hidden', border: '1px solid #e5e7eb', background: '#fff', flexShrink: 0 }}>
                                    {config.imageUrl ? <img src={resolveImageUrl(config.imageUrl)} alt={config.feedbackType} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#cbd5e1' }}><Icon name="image" size={14} /></div>}
                                  </div>
                                  <div>
                                    <div style={{ fontWeight: '600', color: '#1e293b' }}>{formatFeedbackLabel(config.feedbackType)}</div>
                                    <div style={{ marginTop: '4px', color: '#64748b' }}>{config.payoutType}: ₹{config.payoutValue} | Slots {config.remainingSlots}/{config.totalSlots}</div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </td>
                        <td style={{ padding: '12px 16px' }}><span style={{ padding: '4px 8px', borderRadius: '6px', fontSize: '12px', fontWeight: '600', backgroundColor: statusBadge.bg, color: statusBadge.color }}>{statusBadge.label}</span></td>
                        <td style={{ padding: '12px 16px' }}>
                          <div style={{ position: 'relative' }}>
                            <button onClick={() => handleDropdownToggle(product.id)} style={{ padding: '6px 12px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '6px', fontSize: '12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
                              More
                              <Icon name="chevron-down" size={14} />
                            </button>
                            {activeDropdown === product.id && (
                              <div style={{ position: 'absolute', right: 0, top: '100%', background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '8px', zIndex: 20, boxShadow: '0 4px 6px rgba(0,0,0,0.1)', padding: '4px', minWidth: '155px' }}>
                                <button onClick={() => handleView(product)} style={{ width: '100%', padding: '8px', border: 'none', background: 'none', textAlign: 'left', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}><Icon name="eye" size={14} style={{ color: '#3b82f6', opacity: 0.8 }} /> View</button>
                                <button onClick={() => handleEdit(product)} style={{ width: '100%', padding: '8px', border: 'none', background: 'none', textAlign: 'left', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}><Icon name="edit-2" size={14} style={{ color: '#10b981', opacity: 0.8 }} /> Edit</button>
                                {product.restrictedToAssignedMediators !== false && <button onClick={() => handleAssignMediator(product)} style={{ width: '100%', padding: '8px', border: 'none', background: 'none', textAlign: 'left', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}><Icon name="user-plus" size={14} style={{ color: '#8b5cf6', opacity: 0.8 }} /> Assign Mediator</button>}
                                <div style={{ height: '1px', background: '#e2e8f0', margin: '4px 0' }} />
                                <button onClick={() => handleDelete(product)} style={{ width: '100%', padding: '8px', border: 'none', background: 'none', textAlign: 'left', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', color: '#ef4444' }}><Icon name="trash-2" size={14} /> Delete</button>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          <PaginationComp />
        </div>
      </div>

      {showViewModal && selectedProduct && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#ffffff', borderRadius: '16px', width: '90%', maxWidth: '920px', maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '20px 24px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div><h3 style={{ margin: 0, fontSize: '20px', fontWeight: '700' }}>Product Details</h3><p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#64748b' }}>Code: {selectedProduct.productCode}{selectedProduct.asinNumber ? ` | ASIN: ${selectedProduct.asinNumber}` : ''}</p></div>
              <button onClick={() => setShowViewModal(false)} style={{ background: '#f3f4f6', border: 'none', borderRadius: '50%', width: '32px', height: '32px', cursor: 'pointer' }}><Icon name="x" size={20} /></button>
            </div>
            <div style={{ padding: '24px', overflowY: 'auto' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: '20px' }}>
                <div style={{ borderRadius: '14px', overflow: 'hidden', border: '1px solid #e2e8f0', background: '#f8fafc', minHeight: '280px' }}>
                  {selectedProduct.imageUrl ? <img src={resolveImageUrl(selectedProduct.imageUrl)} alt={selectedProduct.productName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <div style={{ width: '100%', height: '100%', minHeight: '280px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#cbd5e1' }}><Icon name="image" size={28} /></div>}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <div style={{ padding: '16px', background: '#f9fafb', borderRadius: '12px' }}>
                    <div style={{ fontSize: '18px', fontWeight: '700', color: '#1e293b' }}>{selectedProduct.productName}</div>
                    <div style={{ marginTop: '6px', fontSize: '13px', color: '#64748b' }}>Brand: {selectedProduct.brandName || '-'} | Platform: {selectedProduct.platformName || '-'}</div>
                    <div style={{ marginTop: '6px', fontSize: '13px', color: '#64748b' }}>Product Amount: ₹{selectedProduct.productAmount ?? '-'}</div>
                    <div style={{ marginTop: '6px', fontSize: '13px', color: '#64748b' }}>{selectedProduct.restrictedToAssignedMediators === false ? 'Open to all active mediators' : 'Restricted to assigned mediators'}</div>
                  </div>
                  <div style={{ padding: '16px', background: '#f9fafb', borderRadius: '12px' }}>
                    <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#374151', textTransform: 'uppercase' }}>Feedback Configurations</h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {(selectedProduct.feedbackConfigs || []).map((config) => (
                        <div key={config.id || config.feedbackType} style={{ display: 'flex', gap: '12px', padding: '12px', borderRadius: '10px', background: '#ffffff', border: '1px solid #e2e8f0' }}>
                          <div style={{ width: '48px', height: '48px', borderRadius: '8px', overflow: 'hidden', border: '1px solid #e5e7eb', background: '#fff', flexShrink: 0 }}>
                            {config.imageUrl ? <img src={resolveImageUrl(config.imageUrl)} alt={config.feedbackType} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#cbd5e1' }}><Icon name="image" size={14} /></div>}
                          </div>
                          <div>
                            <div style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b' }}>{formatFeedbackLabel(config.feedbackType)}</div>
                            <div style={{ marginTop: '6px', fontSize: '13px', color: '#64748b' }}>{config.payoutType}: ₹{config.payoutValue} | Slots {config.remainingSlots}/{config.totalSlots}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div style={{ padding: '16px 24px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end' }}><Button variant="secondary" onClick={() => setShowViewModal(false)}>Close</Button></div>
          </div>
        </div>
      )}

      {showEditModal && selectedProduct && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#ffffff', borderRadius: '12px', padding: '24px', maxWidth: '980px', width: '92%', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, fontSize: '20px', fontWeight: '700', color: '#1e293b' }}>Edit Product</h3>
              <button onClick={() => { setShowEditModal(false); setSelectedProduct(null); setEditFormData({}); setEditProductImageFile(null); setEditProductImagePreview(''); setFeedbackConfigError(''); }} style={{ background: '#f3f4f6', border: 'none', borderRadius: '50%', width: '32px', height: '32px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon name="x" size={20} /></button>
            </div>

            <form onSubmit={handleEditSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500', color: '#374151' }}>Product Name *</label>
                  <input type="text" value={editFormData.productName || ''} onChange={(event) => setEditFormData({ ...editFormData, productName: event.target.value })} required style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px' }} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500', color: '#374151' }}>Product Code *</label>
                  <input type="text" value={editFormData.productCode || ''} onChange={(event) => setEditFormData({ ...editFormData, productCode: event.target.value })} required style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px' }} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500', color: '#374151' }}>ASIN Number</label>
                  <input type="text" value={editFormData.asinNumber || ''} onChange={(event) => setEditFormData({ ...editFormData, asinNumber: event.target.value })} placeholder="e.g., B0XXXXXXX" style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px' }} />
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500', color: '#374151' }}>Product Link</label>
                  <input type="text" value={editFormData.link || ''} onChange={(event) => setEditFormData({ ...editFormData, link: event.target.value })} placeholder="https://example.com/product" style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px' }} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500', color: '#374151' }}>Brand</label>
                  <SearchableSelect
                    options={brandOptions}
                    value={editFormData.brandId || ''}
                    onChange={(value) => setEditFormData({ ...editFormData, brandId: value })}
                    placeholder="Select Brand"
                    searchPlaceholder="Type brand name..."
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500', color: '#374151' }}>Platform</label>
                  <SearchableSelect
                    options={platformOptions}
                    value={editFormData.platformId || ''}
                    onChange={(value) => setEditFormData({ ...editFormData, platformId: value })}
                    placeholder="Select Platform"
                    searchPlaceholder="Type platform name..."
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500', color: '#374151' }}>Product Amount *</label>
                  <input type="number" step="0.01" value={editFormData.productAmount || ''} onChange={(event) => setEditFormData({ ...editFormData, productAmount: event.target.value })} required style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px' }} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500', color: '#374151' }}>Status</label>
                  <select value={editFormData.active !== undefined ? editFormData.active.toString() : ''} onChange={(event) => setEditFormData({ ...editFormData, active: event.target.value === 'true' })} style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px' }}>
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </select>
                </div>
              </div>

              <ImageField
                label="Product Image"
                value={editProductImagePreview || editFormData.imageUrl || ''}
                onChange={(value) => { setEditProductImageFile(null); setEditProductImagePreview(''); setEditFormData({ ...editFormData, imageUrl: value }); }}
                onPickFile={handleProductImagePick}
                onClear={clearEditProductImage}
                fileName={editProductImageFile?.name}
                inputId="edit-product-image-upload"
              />

              <div style={{ padding: '14px 16px', borderRadius: '12px', background: '#f8fbff', border: '1px solid #dbeafe' }}>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                  <input type="checkbox" checked={editFormData.restrictedToAssignedMediators !== false} onChange={(event) => setEditFormData({ ...editFormData, restrictedToAssignedMediators: event.target.checked })} style={{ width: '18px', height: '18px', marginTop: '2px', accentColor: 'var(--primary)' }} />
                  <div>
                    <div style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b' }}>Restrict to selected mediators</div>
                    <div style={{ marginTop: '4px', fontSize: '13px', color: '#64748b' }}>{editFormData.restrictedToAssignedMediators === false ? 'Open deal: all active mediators can submit orders.' : 'Only assigned mediators can submit this deal.'}</div>
                  </div>
                </label>
              </div>

              <FeedbackConfigEditor feedbackConfigs={editFeedbackConfigs} setFeedbackConfigs={setEditFeedbackConfigs} feedbackTypeOptions={feedbackTypeOptions} error={feedbackConfigError} setError={setFeedbackConfigError} />

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#374151' }}><input type="checkbox" checked={editFormData.sellerFeedback || false} onChange={(event) => setEditFormData({ ...editFormData, sellerFeedback: event.target.checked })} style={{ width: '16px', height: '16px' }} />Enable Seller Feedback</label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#374151' }}><input type="checkbox" checked={editFormData.returnWindowClose || false} onChange={(event) => setEditFormData({ ...editFormData, returnWindowClose: event.target.checked })} style={{ width: '16px', height: '16px' }} />Close Return Window</label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#374151' }}><input type="checkbox" checked={editFormData.screenRecording || false} onChange={(event) => setEditFormData({ ...editFormData, screenRecording: event.target.checked })} style={{ width: '16px', height: '16px' }} />Enable Screen Recording</label>
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <Button type="button" variant="secondary" onClick={() => { setShowEditModal(false); setSelectedProduct(null); setEditFormData({}); setEditProductImageFile(null); setEditProductImagePreview(''); setFeedbackConfigError(''); }} disabled={savingEditProduct}>Cancel</Button>
                <Button type="submit" loading={savingEditProduct} disabled={savingEditProduct}>Save Changes</Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showDeleteModal && selectedProduct && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0, 0, 0, 0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#ffffff', borderRadius: '12px', padding: '24px', maxWidth: '400px', width: '90%' }}>
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
              <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: '600', color: '#1e293b' }}>Delete Product</h3>
              <p style={{ margin: 0, fontSize: '14px', color: '#64748b' }}>Are you sure you want to delete <strong>{selectedProduct.productName}</strong> ({selectedProduct.productCode})? This action cannot be undone.</p>
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <Button variant="secondary" onClick={() => { setShowDeleteModal(false); setSelectedProduct(null); }}>Cancel</Button>
              <Button onClick={handleDeleteConfirm} style={{ background: '#ef4444', borderColor: '#ef4444' }}>Delete</Button>
            </div>
          </div>
        </div>
      )}

      <MediatorAssignmentModal isOpen={showAssignModal} onClose={() => { setShowAssignModal(false); setSelectedProduct(null); }} product={selectedProduct} />
    </div>
  );
};

export default AdminProducts;
